import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, Container, Grid, Typography } from "@mui/material";
import Header, { HeaderType } from "../../components/Header";
import {
  useNavigate,
  useParams,
  useSearchParams,
  type NavigateOptions,
} from "react-router-dom";
import {
  GetCurrentForm,
  NavigateTo,
  SetLocation,
  DrawBackgroundTypeEnum,
} from "../../helper/navigation";
import CanvasDraw from "../../components/DrawComponent";
import axios from "axios";
import { environment } from "../../environment/environment";
import { useConfirm } from "../../components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { useAuth, getAccessToken } from "@/context/auth";
import DOMPurify from "dompurify";
import carAccident from "@/assets/draw-templates/CarAccident.png";
import signature from "@/assets/draw-templates/Signature.png";

interface RecordingProps {
  style?: any;
  loading: boolean;
}

type DrawMode = "draw" | "preview";

const DRAW_SURFACE_WIDTH = 600;
const DEFAULT_DRAW_SURFACE_HEIGHT = 400;
const CANVAS_HORIZONTAL_CHROME = 80; // container/card paddings + gutters
const DEFAULT_DRAW_ASPECT = DEFAULT_DRAW_SURFACE_HEIGHT / DRAW_SURFACE_WIDTH;
// CarAccident.png is portrait (4913x8544); preserve that aspect ratio for clarity.
const VEHICLE_DAMAGE_ASPECT = 8544 / 4913;
const BACKGROUND_MAP: Record<DrawBackgroundTypeEnum, string> = {
  [DrawBackgroundTypeEnum.None]: "",
  [DrawBackgroundTypeEnum.Signature]: signature,
  [DrawBackgroundTypeEnum.VehicleDamage]: carAccident,
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const renderQuillHtml = (raw?: string) => {
  const value = raw ?? "";
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  const html = looksLikeHtml
    ? value
    : escapeHtml(value).replace(/\r\n|\r|\n/g, "<br/>");

  return { __html: DOMPurify.sanitize(html) };
};

const AccidentSupportDraw: React.FC<RecordingProps> = () => {
  const navigateFn = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const [sp] = useSearchParams();
  const step = sp.get("step") ?? "0";
  const node = sp.get("node") ?? "";
  const pageKey = `${step}-${node}`;

  const [loading, setLoading] = useState(false);
  const [subHeading, setSubHeading] = useState<string>("");
  const [formDescritpion, setFormDescritpion] = useState<string>("");

  const [isRequired, setIsRequired] = useState(false);
  const [evidenceKey, setEvidenceKey] = useState<string>("");
  const [hasDrawn, setHasDrawn] = useState(false);
  const [hasCurrentStrokes, setHasCurrentStrokes] = useState(false);

  const [bgSrc, setBgSrc] = useState<string | null>(null);
  const [bgReady, setBgReady] = useState(false);
  const [backgroundType, setBackgroundType] = useState<DrawBackgroundTypeEnum>(
    DrawBackgroundTypeEnum.None,
  );

  const cameFromFinal =
    sp.get("from") === "final" ||
    sessionStorage.getItem("returnToFinal") === "1";

  const goToFinal = () => {
    const url = sessionStorage.getItem("finalReturnUrl");
    if (url) {
      navigateFn(url, { replace: true });
      return;
    }
    // fallback (if nothing cached)
    const base = tenant ? `/${tenant}` : "";
    navigateFn(`${base}/accidentSupportFinal`, { replace: true });
  };

  const [saveableCanvas, setSaveableCanvas] = useState<CanvasDraw | null>(null);
  const [, setUploading] = useState(false);
  const confirm = useConfirm();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";

  // NEW: local cache key & cached image
  const [drawCacheKey, setDrawCacheKey] = useState("");
  const [cachedImage, setCachedImage] = useState<string | null>(null);
  const [mode, setMode] = useState<DrawMode>("draw");
  const drawCanvasHostRef = useRef<HTMLDivElement | null>(null);
  const [drawHostWidth, setDrawHostWidth] = useState<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerWidth : DRAW_SURFACE_WIDTH,
  );

  const drawState = useMemo(
    () => ({
      color: "#000000",
      brushRadius: 3,
      lazyRadius: 12,
    }),
    [],
  );

  const canvasSize = useMemo(
    () => {
      const fallbackWidth = Math.max(1, viewportWidth - CANVAS_HORIZONTAL_CHROME);
      const availableWidth = drawHostWidth > 0 ? drawHostWidth : fallbackWidth;
      const width = Math.max(1, Math.min(DRAW_SURFACE_WIDTH, Math.floor(availableWidth)));
      const aspect =
        backgroundType === DrawBackgroundTypeEnum.VehicleDamage
          ? VEHICLE_DAMAGE_ASPECT
          : DEFAULT_DRAW_ASPECT;

      return {
        width: Math.round(width),
        height: Math.round(width * aspect),
      };
    },
    [backgroundType, drawHostWidth, viewportWidth],
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const host = drawCanvasHostRef.current;
    if (!host) return;

    const updateWidth = (w: number) => {
      if (w <= 0) return;
      setDrawHostWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    };

    updateWidth(Math.floor(host.getBoundingClientRect().width));

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver((entries) => {
        const w = Math.floor(entries[0]?.contentRect?.width ?? 0);
        updateWidth(w);
      });
      ro.observe(host);
      return () => ro.disconnect();
    }

    const onResize = () =>
      updateWidth(Math.floor(host.getBoundingClientRect().width));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mode]);

  function go(path: number): void;
  function go(path: string, opts?: NavigateOptions): void;
  function go(path: number | string, opts?: NavigateOptions) {
    if (typeof path === "number") return navigateFn(path);
    const clean = (path as string).replace(/^\/+/, "");
    navigateFn(tenant ? `/${tenant}/${clean}` : `/${clean}`, opts);
  }


  // Helper: go next (no upload)
  const goNext = () => {
    const currentStep = Number(sp.get("step") ?? "0");
    const nextStep = currentStep + 1;
    SetLocation(nextStep);
    go(NavigateTo({ step: nextStep }));
  };

  useEffect(() => {
    const stepIndex = Number(step) || 0;

    SetLocation(stepIndex);

    const current = GetCurrentForm();

    setSubHeading(current?.name || "");
    setFormDescritpion(current?.description || "");

    if (current?.id) {
      localStorage.setItem("formTypeId", JSON.stringify(current.id));
    }

    const req = !!current?.required;
    const evKey = `flowEvidence:${step}:${node}`;
    const cacheKey = `flowDrawCache:${step}:${node}`;

    setIsRequired(req);
    setEvidenceKey(evKey);
    setDrawCacheKey(cacheKey);

    const ev = localStorage.getItem(evKey) === "1";
    setHasDrawn(ev);

    // NEW: hydrate cached image; if exists → preview mode
    const cached = localStorage.getItem(cacheKey);
    if (cached && cached.startsWith("data:image/")) {
      setCachedImage(cached);
      setMode("preview");
    } else {
      setCachedImage(null);
      setMode("draw");
    }

    const nextBackgroundType =
      typeof current?.backgroundType === "number"
        ? (current.backgroundType as DrawBackgroundTypeEnum)
        : DrawBackgroundTypeEnum.None;

    setBackgroundType(nextBackgroundType);
  }, [step, node]);

  useEffect(() => {
    let cancelled = false;

    if (backgroundType === DrawBackgroundTypeEnum.None) {
      setBgSrc(null);
      setBgReady(true);
      return () => {
        cancelled = true;
      };
    }

    const src = BACKGROUND_MAP[backgroundType] ?? carAccident;
    setBgReady(false);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;

    img.onload = () => {
      if (cancelled) return;
      setBgSrc(src);
      setBgReady(true);
    };

    img.onerror = () => {
      setBgSrc(null);
      setBgReady(true);
    };

    return () => {
      cancelled = true;
    };
  }, [backgroundType]);

  const showConfirmDialog = (title: string, message: string) =>
    confirm({ title, description: message, okOnly: true, confirmText: "Ok" });

  const checkUploadStatus = (data: any, uploadedImageDataUrl?: string) => {
    setUploading(false);

    if (data?.status >= 200 && data?.status < 300) {
      // Evidence
      if (isRequired && evidenceKey) {
        localStorage.setItem(evidenceKey, "1");
        setHasDrawn(true);
      }

      // NEW: cache uploaded image per step/node
      if (drawCacheKey && uploadedImageDataUrl) {
        localStorage.setItem(drawCacheKey, uploadedImageDataUrl);
        setCachedImage(uploadedImageDataUrl);
        setMode("preview");
      }

      setLoading(false);

      showConfirmDialog("Success!", "Image Uploaded to Media Library").then(
        () => {
          const s = Number(step ?? "0");
          const doneKey = `flowStepDone:${s}:${node}`;
          localStorage.setItem(doneKey, "1");

          if (cameFromFinal) {
            goToFinal();
            return;
          }
          goNext();
        },
      );
    } else {
      setLoading(false);
      showConfirmDialog(
        "Failed to upload image!",
        "We were unable to upload your image.",
      );
    }
  };

  const uploadDocument = (
    documents: Record<string, unknown>,
    uploadedImageDataUrl?: string,
  ) => {
    setUploading(true);
    setLoading(true);

    axios
      .post(`${environment.baseUrl}/document`, documents, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((resp) => checkUploadStatus(resp, uploadedImageDataUrl))
      .catch(() => {
        showConfirmDialog(
          "Failed to upload image!",
          "We were unable to upload your image.",
        );
        setUploading(false);
        setLoading(false);
      });
  };

  const fetchDocumentImage = () => {
    const capturedImage = saveableCanvas?.getDataURL("png", true, "#ffffff");
    const accidentId = JSON.parse(
      window.localStorage.getItem("accidentId") || "null",
    );

    const current = GetCurrentForm();
    const formId =
      current?.id ?? JSON.parse(localStorage.getItem("formTypeId") || "null");

    const documents = {
      linkedId: accidentId,
      fileName: "digialert_pwa_" + accidentId,
      file: capturedImage,
      type: "png",
      documentTypeId: "00000000-0000-0000-0000-000000000001",
      formId: formId,
    };

    // Pass captured image to cache after upload
    uploadDocument(
      documents,
      typeof capturedImage === "string" ? capturedImage : undefined,
    );
  };

  const clearCachedForThisStep = () => {
    if (drawCacheKey) localStorage.removeItem(drawCacheKey);
    setCachedImage(null);
    setMode("draw");

    if (isRequired && evidenceKey) {
      localStorage.removeItem(evidenceKey);
      setHasDrawn(false);
    }
  };

  const cancel = () => {
    const s = Number(sp.get("step") ?? "0");
    if (s <= 0) {
      go("accidentSupport");
      return;
    }
    const prev = s - 1;
    SetLocation(prev);
    go(NavigateTo({ step: prev }));
  };

  const canContinueRequiredDraw =
    !isRequired || hasDrawn || hasCurrentStrokes || !!cachedImage;
  const showRequiredDrawMessage = isRequired && !canContinueRequiredDraw;

  return (
    <>
      <Header type={HeaderType.AccidentSupportAdd} />

      <div key={pageKey}>
        <Container maxWidth="sm">
          <Grid container spacing={1}>
            <Grid size={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Grid size={12}>
                  <Grid container justifyContent="center">
                    <Typography variant="body1">{subHeading ?? ""}</Typography>
                  </Grid>

                  <br />

                  {formDescritpion && (
                    <Box
                      dangerouslySetInnerHTML={renderQuillHtml(formDescritpion)}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        "&, & *": {
                          maxWidth: "100%",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        },
                        "& p": { mb: 1, mt: 0 },
                        "& h1, & h2, & h3": { mt: 2, mb: 1 },
                        "& ul, & ol": { pl: 3, mb: 1, mt: 0 },
                        "& li": { mb: 0.5 },
                        "& a": { color: "secondary.main" },
                        "& .ql-align-center": { textAlign: "center" },
                        "& .ql-align-right": { textAlign: "right" },
                        "& .ql-align-justify": { textAlign: "justify" },
                      }}
                    />
                  )}

                  <br />
                </Grid>

                {/* NEW: Preview mode */}
                {mode === "preview" && cachedImage ? (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "grey.100",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={cachedImage}
                        alt="saved-drawing"
                        sx={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                          objectFit: "contain",
                        }}
                      />
                    </Box>

                    <Grid container spacing={1} sx={{ mt: 2 }}>
                      <Grid size={6 as any}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          fullWidth
                          disabled={loading}
                          onClick={() => setMode("draw")}
                        >
                          Edit / Redraw
                        </Button>
                      </Grid>
                      <Grid size={6 as any}>
                        <Button
                          variant="contained"
                          color={cameFromFinal ? "primary" : "secondary"}
                          fullWidth
                          disabled={loading || !canContinueRequiredDraw}
                          onClick={cameFromFinal ? goToFinal : goNext}
                        >
                          {cameFromFinal ? "Done" : "Next"}
                        </Button>
                      </Grid>
                    </Grid>

                    {showRequiredDrawMessage && (
                      <Typography sx={{ mt: 1 }} color="error" variant="body2">
                        This step is required. Please complete the drawing before continuing.
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    {/* Draw mode */}
                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Box
                        ref={drawCanvasHostRef}
                        sx={{ width: "100%", display: "flex", justifyContent: "center" }}
                      >
                        {bgReady && (
                          <CanvasDraw
                            key={pageKey}
                            hideGrid
                            hideInterface
                            brushColor={drawState.color}
                            brushRadius={drawState.brushRadius}
                            lazyRadius={drawState.lazyRadius}
                            canvasWidth={canvasSize.width}
                            canvasHeight={canvasSize.height}
                            clampLinesToDocument
                            imgSrc={bgSrc || undefined}
                            immediateLoading
                            ref={(c) => setSaveableCanvas(c)}
                            onChange={(api) => {
                              try {
                                const save = api?.getSaveData?.();
                                if (!save) {
                                  setHasCurrentStrokes(false);
                                  return;
                                }
                                const parsed =
                                  typeof save === "string" ? JSON.parse(save) : save;
                                if (Array.isArray(parsed?.lines)) {
                                  setHasCurrentStrokes(parsed.lines.length > 0);
                                  return;
                                }
                                if (Array.isArray(parsed)) {
                                  setHasCurrentStrokes(parsed.length > 0);
                                  return;
                                }
                                setHasCurrentStrokes(Boolean(save));
                              } catch {
                                setHasCurrentStrokes(false);
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Button
                        variant="contained"
                        color="inherit"
                        disabled={loading || !bgReady}
                        onClick={() => {
                          saveableCanvas?.eraseAll();
                          setHasCurrentStrokes(false);
                          clearCachedForThisStep();
                        }}
                      >
                        Erase
                      </Button>

                      <Button
                        variant="contained"
                        color="inherit"
                        disabled={loading || !bgReady}
                        onClick={() => saveableCanvas?.undo()}
                      >
                        Undo
                      </Button>
                    </Grid>

                    <Grid size={12} sx={{ mt: 2 }}>
                      <Button
                        onClick={() => {
                          fetchDocumentImage();
                        }}
                        variant="contained"
                        color={cameFromFinal ? "primary" : "secondary"}
                        fullWidth
                        disabled={loading || !bgReady || !canContinueRequiredDraw}
                        size="large"
                      >
                        <strong>{cameFromFinal ? "Done" : "Next"}</strong>
                      </Button>

                      {showRequiredDrawMessage && (
                        <Typography sx={{ mt: 1 }} color="error" variant="body2">
                          This step is required. Please complete the drawing before continuing.
                        </Typography>
                      )}
                    </Grid>
                  </>
                )}

                {!cameFromFinal && (
                  <Grid size={12 as any}>
                    <br />
                    <Button
                      type="button"
                      onClick={cancel}
                      value="cancel"
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      size="large"
                    >
                      <strong>Back</strong>
                    </Button>
                  </Grid>
                )}
              </Card>
            </Grid>
          </Grid>
        </Container>
      </div>

      <BrandLoader open={loading} />
    </>
  );
};

export default AccidentSupportDraw;
